import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  projectsApi,
  Artifact,
  ArtifactPreview,
  ProjectCreateInput,
  ProjectDocumentsResponse,
} from "@/services/projectsApi";

export function useProjects() {
  return useQuery({
    queryKey: ["projects"],
    queryFn: projectsApi.list,
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ["projects", id],
    queryFn: () => projectsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (project: ProjectCreateInput) => projectsApi.create(project),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: string } & Parameters<typeof projectsApi.update>[1]) =>
      projectsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", id] });
    },
  });
}

export function useInsertProjectDocuments() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    { projectId: string; documentIds: string[] }
  >({
    mutationFn: ({ projectId, documentIds }) =>
      projectsApi.insertDocuments(projectId, documentIds),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
  });
}

export function useDeleteProjectDocuments() {
  const queryClient = useQueryClient();
  return useMutation<
    ProjectDocumentsResponse,
    Error,
    { projectId: string; documentIds: string[] }
  >({
    mutationFn: ({ projectId, documentIds }) =>
      projectsApi.deleteDocuments(projectId, documentIds),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "documents"] });
    },
  });
}

export function useProjectTemplate(projectId: string | undefined, formatType?: string) {
  return useQuery({
    queryKey: ["projects", projectId, "template", formatType ?? null],
    queryFn: () => projectsApi.getProjectTemplate(projectId!, formatType),
    enabled: !!projectId,
    retry: false,
  });
}

export function useProjectDocumentIds(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "documents"],
    queryFn: () => {
      if (!projectId) return Promise.resolve([]);
      return projectsApi.getDocumentIds(projectId);
    },
    enabled: !!projectId,
    retry: false,
  });
}

export function useTemplateDocuments(
  projectId: string | undefined,
  templateId: string | undefined
) {
  return useQuery({
    queryKey: ["projects", projectId, "templates", templateId, "documents"],
    queryFn: () => {
      if (!projectId || !templateId) return Promise.resolve([]);
      return projectsApi.getTemplateDocuments(projectId, templateId, true);
    },
    enabled: !!projectId && !!templateId,
    retry: false,
  });
}

export function useCreateTemplateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      templateId,
      documentId,
      data,
      status,
    }: {
      projectId: string;
      templateId: string;
      documentId: string;
      data: string | null;
      status?: string;
    }) => projectsApi.createTemplateDocument(projectId, templateId, documentId, data, status),
    onSuccess: (_, { projectId, templateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "templates", templateId, "documents"],
      });
    },
  });
}

export function useUpdateTemplateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      templateId,
      documentDataId,
      data,
      status,
    }: {
      projectId: string;
      templateId: string;
      documentDataId: string;
      data?: string | null;
      status?: string;
    }) => projectsApi.updateTemplateDocument(projectId, templateId, documentDataId, data || null, status),
    onSuccess: (_, { projectId, templateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "templates", templateId, "documents"],
      });
    },
  });
}

export function useDeleteTemplateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      templateId,
      documentDataId,
    }: {
      projectId: string;
      templateId: string;
      documentDataId: string;
    }) => projectsApi.deleteTemplateDocument(projectId, templateId, documentDataId),
    onSuccess: (_, { projectId, templateId }) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", projectId, "templates", templateId, "documents"],
      });
    },
  });
}

export function useTemplateUsageCount(templateId: string | undefined) {
  return useQuery({
    queryKey: ["projects", "usage-count", "template", templateId],
    queryFn: () => projectsApi.getTemplateUsageCount(templateId!),
    enabled: !!templateId,
    retry: false,
  });
}

export function usePromptUsageCount(promptLibraryId: string | undefined) {
  return useQuery({
    queryKey: ["projects", "usage-count", "prompt-library", promptLibraryId],
    queryFn: () => projectsApi.getPromptUsageCount(promptLibraryId!),
    enabled: !!promptLibraryId,
    retry: false,
  });
}

export function useArtifactPreview() {
  return useMutation<ArtifactPreview, Error, { projectId: string; file: File }>({
    mutationFn: ({ projectId, file }) => projectsApi.previewArtifacts(projectId, file),
  });
}

export function useConfirmArtifacts() {
  const queryClient = useQueryClient();
  return useMutation<
    Artifact[],
    Error,
    { projectId: string; file: File; selectedUids: string[] }
  >({
    mutationFn: ({ projectId, file, selectedUids }) =>
      projectsApi.confirmArtifacts(projectId, file, selectedUids),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "artifacts"] });
    },
  });
}

export function useActivateArtifacts() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { projectId: string; uids: string[] }>({
    mutationFn: ({ projectId, uids }) => projectsApi.activateArtifacts(projectId, uids),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "artifacts"] });
    },
  });
}

export function useDeactivateArtifacts() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { projectId: string; uids: string[] }>({
    mutationFn: ({ projectId, uids }) => projectsApi.deactivateArtifacts(projectId, uids),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "artifacts"] });
    },
  });
}

export function useDeleteArtifacts() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { projectId: string; uids: string[] }>({
    mutationFn: ({ projectId, uids }) => projectsApi.deleteArtifacts(projectId, uids),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "artifacts"] });
    },
  });
}

export function useUpdateArtifactDescription() {
  const queryClient = useQueryClient();
  return useMutation<Artifact, Error, { projectId: string; uid: string; description: string }>({
    mutationFn: ({ projectId, uid, description }) =>
      projectsApi.updateArtifactDescription(projectId, uid, description),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "artifacts"] });
    },
  });
}

/** How long an artifact may sit unfinished before we stop expecting it to
 *  finish on its own. Enrichment takes seconds; ten minutes means something
 *  went wrong somewhere we can't see from here. */
const ENRICHMENT_STALL_MS = 10 * 60 * 1000;

export function isArtifactStalled(artifact: Artifact): boolean {
  if (artifact.status !== "Queued" && artifact.status !== "Processing") return false;
  const since = Date.parse(artifact.updatedAt || artifact.createdAt);
  return Number.isFinite(since) && Date.now() - since > ENRICHMENT_STALL_MS;
}

/** `isArtifactStalled`, but it re-renders when the deadline passes.
 *
 *  The plain check reads Date.now() during render, so its value only changes
 *  when something else causes a render — and here nothing does. Polling returns
 *  rows that haven't changed, react-query's structural sharing hands back the
 *  same `data` reference, and a component reading only `data` is never
 *  notified. Polling then stops, because the refetchInterval sees the artifact
 *  as stalled, so the card would sit on "Describing…" indefinitely while the
 *  value backing it was already true.
 *
 *  A timer to the deadline rather than reading dataUpdatedAt: that would mean
 *  a component depending on an unused-looking destructure for its correctness,
 *  and it would re-render every five seconds to catch a moment ten minutes
 *  away. This fires once, at the moment it matters. */
export function useArtifactStalled(artifact: Artifact): boolean {
  const [, setTick] = useState(0);
  const stalled = isArtifactStalled(artifact);
  const pending = artifact.status === "Queued" || artifact.status === "Processing";
  const since = Date.parse(artifact.updatedAt || artifact.createdAt);

  useEffect(() => {
    if (stalled || !pending || !Number.isFinite(since)) return;
    const remaining = Math.max(since + ENRICHMENT_STALL_MS - Date.now(), 0);
    const timer = setTimeout(() => setTick((n) => n + 1), remaining);
    return () => clearTimeout(timer);
  }, [stalled, pending, since]);

  return stalled;
}

export function useArtifacts(projectId: string | undefined) {
  return useQuery({
    queryKey: ["projects", projectId, "artifacts"],
    queryFn: () => projectsApi.getArtifacts(projectId!),
    enabled: !!projectId,
    // Enrichment finishes out of band, so without this a card showing
    // "Describing…" would keep saying so until the page was reloaded.
    //
    // Bounded on purpose. An artifact can be stranded — a message that was
    // never delivered, a worker that isn't running — and polling for it every
    // five seconds forever would cost a request a second per open tab without
    // ever changing anything. Past the cut-off we stop asking and the card
    // says it has stalled instead.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (artifact) =>
          (artifact.status === "Queued" || artifact.status === "Processing") &&
          !isArtifactStalled(artifact)
      )
        ? 5000
        : false,
  });
}
